import utilities from '@/shared/utilities.service';
import {
    B2CResetPasswordTokenByUsernameRequest,
    ChangePasswordFromTokenRequest,
    ValidateTokenRequest,
    ValidateTokenResult,
    B2CClientService,
    ClientResponse,
    LoginRequest,
    LoginResponse,
    CreateProfileResponse,
    CreateProfileRequest,
    ResendRegistrationEmailRequest,
    ActivateProfileRequest,
    ActivateProfileResponse,
    ResendRegistrationEmailResponse,
    ChangeEmailRequestFromTokenRequest,
    ConfirmEmailTokenResult, ConfirmEmailTokenResultType,
} from '@/b2c/shared/api/server-apis';
import siteContextService from '@/b2c/shared/site-context.service';
import authStore from '@/shared/auth/auth.store';

class AuthService {
    async login(username: string, password: string, recaptchaKey: string, messageId?: string): Promise<LoginResponse | undefined> {
        const request: LoginRequest = {
            userName: username,
            password,
            recaptchaResponse: recaptchaKey,
        };

        return B2CClientService.login(request, messageId).then(utilities.unwrapModel);
    };

    async createProfile(params: Omit<CreateProfileRequest, 'profileRegistrationSettings' | 'recaptchaResponse'>, recaptchaResponse: string, messageId?: string): Promise<CreateProfileResponse | undefined> {
        const request: CreateProfileRequest = {
            ...params,
            profileRegistrationSettings: siteContextService.profileActivationSettings,
            recaptchaResponse,
        };
        return B2CClientService.createProfile(request, messageId).then(utilities.unwrapModel);
    };

    async resendRegistrationEmail(profileRegistrationId: string, messageId?: string): Promise<ResendRegistrationEmailResponse | undefined> {
        const request: ResendRegistrationEmailRequest = {
            profileRegistrationId,
            profileRegistrationSettings: siteContextService.profileActivationSettings,
        };
        return B2CClientService.resendRegistrationEmail(request, messageId).then(utilities.unwrapModel);
    };

    async activateProfile(activationToken: string, messageId?: string): Promise<ActivateProfileResponse | undefined> {
        const request: ActivateProfileRequest = {
            activationToken,
        };
        return B2CClientService.activateProfile(request, messageId).then(utilities.unwrapModel);
    };

    async validateResetPasswordToken(resetToken: string): Promise<ValidateTokenResult | undefined> {
        const request: ValidateTokenRequest = {
            resetToken,
        };
        const response = await B2CClientService.validateResetToken(request).then(utilities.unwrapModel);
        return response;
    }

    async validateChangeEmailToken(confirmEmailToken: string): Promise<ConfirmEmailTokenResult | undefined> {
        const request: ChangeEmailRequestFromTokenRequest = {
            confirmEmailToken,
        };
        const response = await B2CClientService.changeEmailFromEmailConfirmToken(request).then(utilities.unwrapModel);
        if (response?.result && response.result === ConfirmEmailTokenResultType.Success) {
            await authStore.tryRefreshExpiredAccessToken();
        }
        return response;
    }

    async resetPassword(resetToken: string, password: string, passwordConfirmation: string): Promise<ValidateTokenResult | undefined> {
        const request: ChangePasswordFromTokenRequest = {
            resetToken,
            password,
            passwordConfirmation,
        };
        const response = await B2CClientService.resetPassword(request).then(utilities.unwrapModel);
        return response;
    }

    async resetPasswordTokenByEmail(email: string, recaptchaKey: string): Promise<ClientResponse> {
        const request: B2CResetPasswordTokenByUsernameRequest = {
            userName: email,
            requestSettings: siteContextService.resetPasswordSettings,
            recaptchaResponse: recaptchaKey,
        };
        const response = await B2CClientService.resetPasswordTokenByEmail(request);
        return response;
    }
}

export default new AuthService();
